import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CustomAgentsStore } from '../../shared/services/studio/custom-agents.store';
import { TenantContextService } from '../tenant/tenant-context.service';
import { TenantLifecycleService } from '../tenant/tenant-lifecycle.service';
import {
  AGENTS_NAV_FLAG_KEY,
  FeatureFlagService,
  INSIGHT_STUDIO_FLAG_KEY,
} from './feature-flag.service';

/**
 * Gate for `/agents/custom/:slug`.
 *
 * Same flag as the static agent routes plus the Insight Studio flag, then the
 * tenant bootstrap (the agent list is tenant-scoped), then the slug must resolve
 * to a published agent — or to a draft when the caller is privileged and may
 * preview it.
 */
export const customAgentGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const flags = inject(FeatureFlagService);
  const router = inject(Router);
  const lifecycle = inject(TenantLifecycleService);
  const tenantContext = inject(TenantContextService);
  const store = inject(CustomAgentsStore);
  const home = () => router.createUrlTree(['/home']);

  await flags.ready();
  if (!flags.isEnabled(AGENTS_NAV_FLAG_KEY) || !flags.isEnabled(INSIGHT_STUDIO_FLAG_KEY)) {
    return home();
  }

  try {
    await firstValueFrom(lifecycle.bootstrapTenantContext());
  } catch {
    // No tenant context to act on — deny conservatively.
    return home();
  }

  // A tenant switch mid-load invalidates the store; keep waiting until a list survives one.
  do {
    await store.ready();
  } while (!store.loaded());
  const row = store.bySlug(route.paramMap.get('slug') ?? '')();
  if (!row) return home();
  return row.published !== null || (row.draft !== null && tenantContext.isPrivileged()) || home();
};
